import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { APP_CONSTANTS } from './constants';
import { Header } from './components/ui';
import { tokens } from './styles/tokens';

// Helper to convert decimal odds to American odds
function decimalToAmerican(decimal) {
  if (!decimal || decimal === 0) return '';
  if (decimal >= 2) {
    return `+${Math.round((decimal - 1) * 100)}`;
  } else {
    return `-${Math.round(100 / (decimal - 1))}`;
  }
}

function SportsDetailPage() {
  const { sport } = useParams();
  const navigate = useNavigate();
  const { 
    isAuthenticated, 
    loading: authLoading, 
    logout, 
    userInfo
  } = useAuth();
  
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Sport configuration
  const sportConfig = {
    'MLB': {
      name: 'Major League Baseball',
      icon: '/logos/MLB/_MLB_logo.png',
      apiKey: 'baseball_mlb',
      color: tokens.colors.primary[400],
      bgGradient: `linear-gradient(135deg, ${tokens.colors.primary[900]}33 0%, ${tokens.colors.primary[800]}22 100%)`
    },
    'soccer_epl': {
      name: 'Premier League',
      icon: '/logos/Premier_League/_PremierLeague_logo.png',
      apiKey: 'soccer_epl',
      color: tokens.colors.secondary[400],
      bgGradient: `linear-gradient(135deg, ${tokens.colors.secondary[900]}33 0%, ${tokens.colors.secondary[800]}22 100%)`
    },
    'basketball_nba': {
      name: 'National Basketball Association',
      icon: '/logos/NBA/_NBA_logo.png',
      apiKey: 'basketball_nba',
      color: tokens.colors.accent[400],
      bgGradient: `linear-gradient(135deg, ${tokens.colors.accent[900]}33 0%, ${tokens.colors.accent[800]}22 100%)`
    }
  };

  const currentSport = sportConfig[sport] || sportConfig['MLB'];

  // Fetch games data
  useEffect(() => {
    const fetchGames = async () => {
      const sportData = sportConfig[sport] || sportConfig['MLB'];
      if (!sportData) return;
      
      try {
        setLoading(true);
        setError('');

        const response = await fetch(
          `https://api.the-odds-api.com/v4/sports/${sportData.apiKey}/odds/?apiKey=${APP_CONSTANTS.ODDS_API_KEY}&regions=us&markets=h2h,spreads,totals`
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch games: ${response.status}`);
        }

        const data = await response.json();
        const now = new Date();
        
        // Get next 10 games instead of next 2 days (for out of season sports)
        const upcomingGames = data
          .filter(game => new Date(game.commence_time) >= now)
          .sort((a, b) => new Date(a.commence_time) - new Date(b.commence_time))
          .slice(0, 10);

        setGames(upcomingGames);
      } catch (err) {
        console.error('Error fetching games:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchGames();
    }
  }, [sport, isAuthenticated]);

  const formatGameTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    
    if (date.toDateString() === now.toDateString()) {
      return `Today ${date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true 
      })}`;
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return `Tomorrow ${date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true 
      })}`;
    } else {
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
  };

  // Team logo mapping
  const teamLogoMapping = {
    // American League East
    "New York Yankees": "yankees.png",
    "Boston Red Sox": "redSox.png",
    "Tampa Bay Rays": "rays.png",
    "Toronto Blue Jays": "blueJays.png",
    "Baltimore Orioles": "orioles.png",
    
    // American League Central  
    "Chicago White Sox": "whiteSox.png",
    "Detroit Tigers": "tigers.png",
    "Kansas City Royals": "royals.png",
    "Minnesota Twins": "twins.png",
    "Cleveland Guardians": "indians.png",
    
    // American League West
    "Houston Astros": "astros.png",
    "Los Angeles Angels": "angels.png",
    "Oakland Athletics": "athletics.png",
    "Seattle Mariners": "mariners.png",
    "Texas Rangers": "rangers.png",
    
    // National League East
    "Atlanta Braves": "braves.png",
    "New York Mets": "mets.png",
    "Philadelphia Phillies": "phillies.png",
    "Washington Nationals": "nationals.png",
    "Miami Marlins": "marlins.png",
    
    // National League Central
    "Chicago Cubs": "cubs.png",
    "Milwaukee Brewers": "brewers.png",
    "St. Louis Cardinals": "cardinals.png",
    "Pittsburgh Pirates": "pirates.png",
    "Cincinnati Reds": "reds.png",
    
    // National League West
    "Los Angeles Dodgers": "dodgers.png",
    "San Francisco Giants": "giants.png",
    "San Diego Padres": "padres.png",
    "Arizona Diamondbacks": "diamondbacks.png",
    "Colorado Rockies": "rockies.png"
  };

  const getTeamLogo = (teamName) => {
    const logoFile = teamLogoMapping[teamName];
    return logoFile ? `/logos/MLB/${logoFile}` : null;
  };

  const getTeamColors = (teamName) => {
    const hash = teamName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const hue = hash % 360;
    return {
      primary: `hsl(${hue}, 60%, 55%)`,
      secondary: `hsl(${hue}, 60%, 45%)`,
      light: `hsl(${hue}, 60%, 65%)`
    };
  };

  // EPL team logo mapping
const eplTeamLogoMapping = {
  // Direct matches
  "Liverpool": "liverpool.png",
  "Bournemouth": "bournemouth.png", 
  "Aston Villa": "astonVilla.png",
  "Newcastle United": "newcastleUnited.png",
  "Fulham": "fulham.png",
  "Tottenham Hotspur": "tottenhamHotspur.png",
  "Burnley": "burnley.png",
  "West Ham United": "westHamUnited.png",
  "Manchester City": "manchesterCity.png",
  
  // Special cases
  "Brighton and Hove Albion": "brighton.png",
  "Wolverhampton Wanderers": "wolves.png", 
  "Sunderland": "sunderlandAfc.png",
  
  // Additional teams with logos available
  "Arsenal": "arsenal.png",
  "Chelsea": "chelsea.png", 
  "Manchester United": "manchesterUnited.png",
  "Leicester City": "leicesterCity.png",
  "Everton": "everton.png",
  "Crystal Palace": "crystalPalace.png",
  "Southampton": "southampton.png",
  
  // Historical/Championship teams (available but may not be in current EPL)
  "Cardiff City": "cardiffCity.png",
  "Huddersfield Town": "huddersfield.png",
  "Norwich City": "norwichCity.png",
  "Watford": "watford.png",
  "West Bromwich Albion": "westBromwichAlbion.png",
  "Stoke City": "stokeCity.png",
  "Swansea City": "swanseaCityAfc.png"
};

const getEplTeamLogo = (teamName) => {
  const logoFile = eplTeamLogoMapping[teamName];
  return logoFile ? `/logos/Premier_League/${logoFile}` : null;
};

// NBA team logo mapping
const nbaTeamLogoMapping = {
  // Eastern Conference - Atlantic Division
  "Boston Celtics": "celtics.png",
  "Brooklyn Nets": "nets.png",
  "New York Knicks": "knicks.png",
  "Philadelphia 76ers": "76ers.png",
  "Toronto Raptors": "raptors.png",
  
  // Eastern Conference - Central Division
  "Chicago Bulls": "bulls.png",
  "Cleveland Cavaliers": "cavaliers.png",
  "Detroit Pistons": "pistons.png",
  "Indiana Pacers": "pacers.png",
  "Milwaukee Bucks": "bucks.png",
  
  // Eastern Conference - Southeast Division
  "Atlanta Hawks": "hawks.png",
  "Charlotte Hornets": "hornets.png",
  "Miami Heat": "heat.png",
  "Orlando Magic": "magic.png",
  "Washington Wizards": "wizards.png",
  
  // Western Conference - Northwest Division
  "Denver Nuggets": "nuggets.png",
  "Minnesota Timberwolves": "timberwolves.png",
  "Oklahoma City Thunder": "thunder.png",
  "Portland Trail Blazers": "trailBlazers.png",
  "Utah Jazz": "jazz.png",
  
  // Western Conference - Pacific Division
  "Golden State Warriors": "warriors.png",
  "Los Angeles Clippers": "clippers.png",
  "Los Angeles Lakers": "lakers.png",
  "Phoenix Suns": "suns.png",
  "Sacramento Kings": "kings.png",
  
  // Western Conference - Southwest Division
  "Dallas Mavericks": "mavericks.png",
  "Houston Rockets": "rockets.png",
  "Memphis Grizzlies": "grizzlies.png",
  "New Orleans Pelicans": "pelicans.png",
  "San Antonio Spurs": "spurs.png"
};

const getNbaTeamLogo = (teamName) => {
  const logoFile = nbaTeamLogoMapping[teamName];
  return logoFile ? `/logos/NBA/${logoFile}` : null;
};

const getTeamInitials = (teamName) => {
  return teamName.split(' ').map(word => word[0]).join('').slice(0, 3);
};

  if (authLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: tokens.colors.gradients.dark,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: `3px solid ${tokens.colors.neutral[800]}`,
            borderTopColor: tokens.colors.accent[400],
            borderRadius: '50%',
            margin: '0 auto',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ color: tokens.colors.neutral[400], marginTop: tokens.spacing[4] }}>
            Loading...
          </p>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  const menuItems = [
    { label: 'Home', href: '/home' },
    { label: 'Sports', href: '/sports', active: true },
    { label: 'My Bets', href: '/my-bets' }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: tokens.colors.gradients.dark,
      position: 'relative'
    }}>
      <Header
        logo="ELXR Sportsbook"
        user={userInfo ? {
          name: userInfo.given_name || userInfo.name,
          email: userInfo.email
        } : null}
        onLogout={handleLogout}
        menuItems={menuItems}
      />

      {/* Animated Background */}
      <div style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none'
      }}>
        <div style={{
          position: 'absolute',
          top: '20%',
          left: '5%',
          width: '400px',
          height: '400px',
          background: `radial-gradient(circle, ${tokens.colors.primary[900]}15 0%, transparent 70%)`,
          filter: 'blur(60px)',
          animation: 'float 25s ease-in-out infinite'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '10%',
          right: '10%',
          width: '500px',
          height: '500px',
          background: `radial-gradient(circle, ${tokens.colors.accent[900]}15 0%, transparent 70%)`,
          filter: 'blur(60px)',
          animation: 'float 30s ease-in-out infinite reverse'
        }} />
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          33% { transform: translate(30px, -30px) rotate(120deg); }
          66% { transform: translate(-20px, 20px) rotate(240deg); }
        }
      `}</style>

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: `${tokens.spacing[8]} ${tokens.spacing[6]}`,
        position: 'relative',
        zIndex: 1
      }}>
        {/* Back Button & Header */}
        <div style={{ marginBottom: tokens.spacing[8] }}>
          <button
            onClick={() => navigate('/sports')}
            style={{
              background: `${tokens.colors.dark[800]}88`,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${tokens.colors.neutral[800]}`,
              color: tokens.colors.neutral[400],
              padding: `${tokens.spacing[3]} ${tokens.spacing[5]}`,
              borderRadius: tokens.borderRadius.lg,
              display: 'flex',
              alignItems: 'center',
              gap: tokens.spacing[2],
              marginBottom: tokens.spacing[6],
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontWeight: tokens.typography.fontWeight.medium
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = tokens.colors.accent[400];
              e.currentTarget.style.borderColor = tokens.colors.accent[700];
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = tokens.colors.neutral[400];
              e.currentTarget.style.borderColor = tokens.colors.neutral[800];
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Sports
          </button>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.spacing[4],
            marginBottom: tokens.spacing[6]
          }}>
                         <div style={{
               background: currentSport.bgGradient,
               borderRadius: tokens.borderRadius['2xl'],
               padding: tokens.spacing[4],
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               minWidth: '80px',
               minHeight: '80px'
             }}>
                           <img 
              src={currentSport.icon} 
              alt={currentSport.name}
              style={{
                width: '64px',
                height: '64px',
                objectFit: 'contain'
              }}
            />
             </div>
            <div>
              <h1 style={{
                fontSize: tokens.typography.fontSize['4xl'],
                fontWeight: tokens.typography.fontWeight.extrabold,
                background: tokens.colors.gradients.glow,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: tokens.spacing[2]
              }}>
                {currentSport.name}
              </h1>
              <p style={{
                color: tokens.colors.neutral[400],
                fontSize: tokens.typography.fontSize.lg
              }}>
                Games in the next 2 days
              </p>
            </div>
          </div>
        </div>

        {/* Games List */}
        {loading ? (
          <div style={{
            background: `linear-gradient(135deg, ${tokens.colors.dark[800]}99 0%, ${tokens.colors.dark[900]}99 100%)`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${tokens.colors.neutral[800]}`,
            borderRadius: tokens.borderRadius.xl,
            padding: tokens.spacing[12],
            textAlign: 'center'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: `3px solid ${tokens.colors.neutral[800]}`,
              borderTopColor: tokens.colors.accent[400],
              borderRadius: '50%',
              margin: '0 auto',
              animation: 'spin 1s linear infinite'
            }} />
            <p style={{
              color: tokens.colors.neutral[400],
              marginTop: tokens.spacing[4]
            }}>
              Loading games...
            </p>
          </div>
        ) : error ? (
          <div style={{
            background: `${tokens.colors.error[900]}33`,
            border: `1px solid ${tokens.colors.error[700]}`,
            borderRadius: tokens.borderRadius.xl,
            padding: tokens.spacing[8],
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '3rem',
              marginBottom: tokens.spacing[4]
            }}>
              ⚠️
            </div>
            <h3 style={{
              color: tokens.colors.error[400],
              fontSize: tokens.typography.fontSize.xl,
              marginBottom: tokens.spacing[4]
            }}>
              Failed to load games
            </h3>
            <p style={{
              color: tokens.colors.neutral[500],
              fontSize: tokens.typography.fontSize.sm
            }}>
              {error}
            </p>
          </div>
        ) : games.length === 0 ? (
          <div style={{
            background: `linear-gradient(135deg, ${tokens.colors.dark[800]}99 0%, ${tokens.colors.dark[900]}99 100%)`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${tokens.colors.neutral[800]}`,
            borderRadius: tokens.borderRadius.xl,
            padding: tokens.spacing[12],
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '4rem',
              marginBottom: tokens.spacing[6],
              opacity: 0.3
                         }}>
                           <img 
              src={currentSport.icon} 
              alt={currentSport.name}
              style={{
                width: '48px',
                height: '48px',
                objectFit: 'contain'
              }}
            />
             </div>
            <h3 style={{
              color: tokens.colors.neutral[300],
              fontSize: tokens.typography.fontSize.xl,
              fontWeight: tokens.typography.fontWeight.semibold,
              marginBottom: tokens.spacing[4]
            }}>
              No games available
            </h3>
            <p style={{
              color: tokens.colors.neutral[500],
              fontSize: tokens.typography.fontSize.base
            }}>
              There are no {currentSport.name.toLowerCase()} games scheduled in the next 2 days.
            </p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gap: tokens.spacing[6]
          }}>
            {games.map((game, index) => {
              const homeColors = getTeamColors(game.home_team);
              const awayColors = getTeamColors(game.away_team);
              const topBook = game.bookmakers?.[0];
              const h2hMarket = topBook?.markets?.find(m => m.key === 'h2h');
              
              return (
                <div 
                  key={index}
                  onClick={() => navigate(`/game/${game.id}`, { state: { game, sport: sport } })}
                  style={{
                    background: `linear-gradient(135deg, ${tokens.colors.dark[800]}99 0%, ${tokens.colors.dark[900]}99 100%)`,
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${tokens.colors.neutral[800]}`,
                    borderRadius: tokens.borderRadius.xl,
                    padding: tokens.spacing[8],
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = tokens.colors.accent[700];
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = `0 15px 35px ${tokens.colors.dark[900]}66`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = tokens.colors.neutral[800];
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* Decorative gradient stripe */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: tokens.colors.gradients.accent
                  }} />

                                     <div style={{
                     display: 'grid',
                     gridTemplateColumns: '1fr auto 1fr',
                     alignItems: 'center',
                     gap: tokens.spacing[6]
                   }}>
                                            {/* Home Team */}
                       <div style={{ textAlign: 'center' }}>
                         <div style={{
                           width: '80px',
                           height: '80px',
                           borderRadius: tokens.borderRadius.lg,
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           margin: '0 auto',
                           marginBottom: tokens.spacing[3],
                           fontSize: tokens.typography.fontSize.xl,
                           fontWeight: tokens.typography.fontWeight.bold,
                           color: homeColors.primary
                         }}>
                           {((sport === 'MLB' && getTeamLogo(game.home_team)) || (sport === 'soccer_epl' && getEplTeamLogo(game.home_team)) || (sport === 'basketball_nba' && getNbaTeamLogo(game.home_team))) ? (
                             <img
                               src={sport === 'MLB' ? getTeamLogo(game.home_team) : sport === 'soccer_epl' ? getEplTeamLogo(game.home_team) : getNbaTeamLogo(game.home_team)}
                               alt={game.home_team}
                               style={{
                                 width: '80px',
                                 height: '80px',
                                 objectFit: 'contain'
                               }}
                               onError={(e) => {
                                 e.target.style.display = 'none';
                                 e.target.nextSibling.style.display = 'flex';
                               }}
                             />
                           ) : null}
                           <div style={{
                             display: ((sport === 'MLB' && getTeamLogo(game.home_team)) || (sport === 'soccer_epl' && getEplTeamLogo(game.home_team)) || (sport === 'basketball_nba' && getNbaTeamLogo(game.home_team))) ? 'none' : 'flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                             width: '100%',
                             height: '100%',
                             background: `linear-gradient(135deg, ${homeColors.primary}33 0%, ${homeColors.secondary}22 100%)`,
                             border: `2px solid ${homeColors.primary}44`,
                             borderRadius: tokens.borderRadius.lg
                           }}>
                             {getTeamInitials(game.home_team)}
                           </div>
                         </div>
                       <h3 style={{
                         color: tokens.colors.neutral[0],
                         fontSize: tokens.typography.fontSize.lg,
                         fontWeight: tokens.typography.fontWeight.bold,
                         marginBottom: tokens.spacing[1]
                       }}>
                         {game.home_team}
                       </h3>
                       {h2hMarket && (
                         <div style={{
                           background: `${homeColors.primary}22`,
                           border: `1px solid ${homeColors.primary}44`,
                           borderRadius: tokens.borderRadius.lg,
                           padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
                           display: 'inline-block'
                         }}>
                           <span style={{
                             color: homeColors.primary,
                             fontSize: tokens.typography.fontSize.lg,
                             fontWeight: tokens.typography.fontWeight.bold
                           }}>
                             {(() => {
                               const homeOutcome = h2hMarket.outcomes?.find(o => o.name === game.home_team);
                               return homeOutcome ? decimalToAmerican(homeOutcome.price) : 'N/A';
                             })()}
                           </span>
                         </div>
                       )}
                     </div>

                    {/* Game Info */}
                    <div style={{
                      textAlign: 'center',
                      padding: tokens.spacing[4],
                      background: `${tokens.colors.dark[700]}44`,
                      borderRadius: tokens.borderRadius.lg,
                      border: `1px solid ${tokens.colors.neutral[800]}`,
                      minWidth: '200px'
                    }}>
                      <p style={{
                        color: tokens.colors.neutral[500],
                        fontSize: tokens.typography.fontSize.sm,
                        fontWeight: tokens.typography.fontWeight.semibold,
                        textTransform: 'uppercase',
                        marginBottom: tokens.spacing[2]
                      }}>
                        {formatGameTime(game.commence_time)}
                      </p>
                      <div style={{
                        color: tokens.colors.neutral[300],
                        fontSize: tokens.typography.fontSize['2xl'],
                        fontWeight: tokens.typography.fontWeight.bold,
                        marginBottom: tokens.spacing[2]
                      }}>
                        VS
                      </div>
                      {topBook && (
                        <p style={{
                          color: tokens.colors.neutral[400],
                          fontSize: tokens.typography.fontSize.xs
                        }}>
                          via {topBook.title}
                        </p>
                      )}
                    </div>

                                                                                     {/* Away Team */}
                       <div style={{ textAlign: 'center' }}>
                         <div style={{
                           width: '80px',
                           height: '80px',
                           borderRadius: tokens.borderRadius.lg,
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           margin: '0 auto',
                           marginBottom: tokens.spacing[3],
                           fontSize: tokens.typography.fontSize.xl,
                           fontWeight: tokens.typography.fontWeight.bold,
                           color: awayColors.primary
                         }}>
                           {((sport === 'MLB' && getTeamLogo(game.away_team)) || (sport === 'soccer_epl' && getEplTeamLogo(game.away_team)) || (sport === 'basketball_nba' && getNbaTeamLogo(game.away_team))) ? (
                             <img
                               src={sport === 'MLB' ? getTeamLogo(game.away_team) : sport === 'soccer_epl' ? getEplTeamLogo(game.away_team) : getNbaTeamLogo(game.away_team)}
                               alt={game.away_team}
                               style={{
                                 width: '80px',
                                 height: '80px',
                                 objectFit: 'contain'
                               }}
                               onError={(e) => {
                                 e.target.style.display = 'none';
                                 e.target.nextSibling.style.display = 'flex';
                               }}
                             />
                           ) : null}
                           <div style={{
                             display: ((sport === 'MLB' && getTeamLogo(game.away_team)) || (sport === 'soccer_epl' && getEplTeamLogo(game.away_team)) || (sport === 'basketball_nba' && getNbaTeamLogo(game.away_team))) ? 'none' : 'flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                             width: '100%',
                             height: '100%',
                             background: `linear-gradient(135deg, ${awayColors.primary}33 0%, ${awayColors.secondary}22 100%)`,
                             border: `2px solid ${awayColors.primary}44`,
                             borderRadius: tokens.borderRadius.lg
                           }}>
                             {getTeamInitials(game.away_team)}
                           </div>
                         </div>
                       <h3 style={{
                         color: tokens.colors.neutral[0],
                         fontSize: tokens.typography.fontSize.lg,
                         fontWeight: tokens.typography.fontWeight.bold,
                         marginBottom: tokens.spacing[1]
                       }}>
                         {game.away_team}
                       </h3>
                       {h2hMarket && (
                         <div style={{
                           background: `${awayColors.primary}22`,
                           border: `1px solid ${awayColors.primary}44`,
                           borderRadius: tokens.borderRadius.lg,
                           padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
                           display: 'inline-block'
                         }}>
                           <span style={{
                             color: awayColors.primary,
                             fontSize: tokens.typography.fontSize.lg,
                             fontWeight: tokens.typography.fontWeight.bold
                           }}>
                             {(() => {
                               const awayOutcome = h2hMarket.outcomes?.find(o => o.name === game.away_team);
                               return awayOutcome ? decimalToAmerican(awayOutcome.price) : 'N/A';
                             })()}
                           </span>
                         </div>
                       )}
                     </div>
                  </div>

                  {/* Additional markets preview */}
                  {topBook && topBook.markets && topBook.markets.length > 1 && (
                    <div style={{
                      marginTop: tokens.spacing[6],
                      padding: tokens.spacing[4],
                      background: `${tokens.colors.dark[700]}22`,
                      borderRadius: tokens.borderRadius.lg,
                      textAlign: 'center'
                    }}>
                      <p style={{
                        color: tokens.colors.neutral[500],
                        fontSize: tokens.typography.fontSize.xs,
                        marginBottom: tokens.spacing[2]
                      }}>
                        {topBook.markets.length} betting markets available
                      </p>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: tokens.spacing[3],
                        flexWrap: 'wrap'
                      }}>
                        {topBook.markets.slice(0, 3).map((market, idx) => (
                          <span
                            key={idx}
                            style={{
                              background: `${tokens.colors.accent[900]}33`,
                              color: tokens.colors.accent[400],
                              padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
                              borderRadius: tokens.borderRadius.sm,
                              fontSize: tokens.typography.fontSize.xs,
                              fontWeight: tokens.typography.fontWeight.medium
                            }}
                          >
                            {market.key.replace('_', ' ').toUpperCase()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default SportsDetailPage; 